/**
 * The heartbeat an office TV sends after it signs in.
 *
 * **This is the seventh module allowed to touch `localStorage`.** It stores
 * one device id the screen made for itself, and nothing else. A device id is
 * not a credential. See `docs/decisions/0021-a-display-keeps-its-device-id.md`.
 *
 * `.oxlintrc.json` exempts this file for `localStorage` and nothing else.
 * `sessionStorage`, `indexedDB` and `caches` still fail the gate here.
 *
 * Every storage call is wrapped. A browser that throws keeps one id in
 * module memory for this page load, so the screen still reports as one device.
 */

import { api } from './api.ts'
import { currentDisplayPage, type DisplaySession } from './display-mode.ts'

/**
 * How often a signed-in screen reports itself.
 *
 * A product decision, not a design value. One minute, and the Displays page
 * calls a screen offline after three. `display-heartbeat.test.ts` pins the
 * value, so changing it stays a deliberate act.
 */
export const DISPLAY_HEARTBEAT_MS = 60_000

/** The only key this module writes. */
export const DISPLAY_DEVICE_KEY = 'blytz-display-device-id'

/** The device id the heartbeat endpoint accepts. */
const DEVICE_ID = /^[A-Za-z0-9_-]{8,64}$/

/** The id kept when storage throws, for this page load only. */
let memoryId: string | null = null

/**
 * A device id this browser can make.
 *
 * `randomUUID` is missing on older TV browsers and outside a secure context.
 * Sixteen random bytes as hex still match the device id rule. `Math.random`
 * is the last resort, when `crypto` itself is missing.
 *
 * @returns A new device id.
 */
function newDeviceId(): string {
  const webCrypto = globalThis.crypto
  if (webCrypto !== undefined && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (
    webCrypto !== undefined &&
    typeof webCrypto.getRandomValues === 'function'
  ) {
    webCrypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }

  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
}

/** What a screen says it is showing. */
export interface HeartbeatShowing {
  path: string
  index: number
  count: number
}

/** The body `POST /display-heartbeat` accepts. */
export interface HeartbeatBody {
  deviceId: string
  name: string
  state: 'starting' | 'playing' | 'not-set-up'
  bundle?: string | null
  viewport?: { width: number; height: number } | null
  showing: HeartbeatShowing | null
}

/**
 * The device id this screen keeps.
 *
 * A stored id that matches the rule is reused. Anything else is replaced.
 * When storage throws, one id is kept in memory for the page load. A later
 * call whose storage works prefers what storage holds.
 *
 * @returns The device id to send.
 */
export function displayDeviceId(): string {
  let stored: string | null
  try {
    stored = localStorage.getItem(DISPLAY_DEVICE_KEY)
  } catch {
    memoryId ??= newDeviceId()

    return memoryId
  }
  if (stored !== null && DEVICE_ID.test(stored)) {
    return stored
  }

  const made = newDeviceId()
  try {
    localStorage.setItem(DISPLAY_DEVICE_KEY, made)

    return made
  } catch {
    memoryId ??= made

    return memoryId
  }
}

/**
 * The heartbeat to send for this session, or null when nothing should be sent.
 *
 * `off` and `offline` send nothing. `booting` is starting. `missing` is not
 * set up. `playing` and `refreshing` are playing, on the page now on screen.
 * A playing session with no current page sends nothing: the body would be
 * one the service refuses.
 *
 * @param session - The display session now.
 * @param deviceId - The id `displayDeviceId` returned.
 * @returns The body, or null.
 */
export function heartbeatBody(
  session: DisplaySession,
  deviceId: string
): HeartbeatBody | null {
  if (session.kind === 'off' || session.kind === 'offline') {
    return null
  }
  if (session.kind === 'booting') {
    return { deviceId, name: session.name, state: 'starting', showing: null }
  }
  if (session.kind === 'missing') {
    return {
      deviceId,
      name: session.name,
      state: 'not-set-up',
      showing: null
    }
  }

  const page = currentDisplayPage(session)
  if (page === null) {
    return null
  }

  return {
    deviceId,
    name: session.name,
    state: 'playing',
    showing: {
      path: page.path,
      index: session.index,
      count: session.pages.length
    }
  }
}

/**
 * The fields a heartbeat reports, or null when nothing should be sent.
 *
 * The device id is left out, so a render can depend on this without reading
 * storage. `shownAt` and `theme` are left out too: they do not change what
 * the screen tells the service.
 *
 * @param session - The display session now.
 * @returns A stable string, or null.
 */
export function heartbeatReportKey(session: DisplaySession): string | null {
  const body = heartbeatBody(session, 'device-id')
  if (body === null) {
    return null
  }

  return JSON.stringify({
    name: body.name,
    state: body.state,
    showing: body.showing
  })
}

/** The server time and latest refresh request returned by a heartbeat. */
export interface HeartbeatAnswer {
  deviceId: string
  lastSeenAt: string
  reloadRequestedAt: string | null
}

/** Read the entry script filename. Missing or unsafe names remain unknown. */
export function bundleId(): string | null {
  try {
    const src = document.querySelector<HTMLScriptElement>(
      'script[type="module"][src]'
    )?.src
    if (!src) return null
    const name = new URL(src).pathname.split('/').at(-1)
    return name && /^[A-Za-z0-9._-]{1,128}$/.test(name) ? name : null
  } catch {
    return null
  }
}

/** Send a report and return its parsed answer; failures never interrupt playback. */
export async function sendDisplayHeartbeat(
  body: HeartbeatBody
): Promise<HeartbeatAnswer | null> {
  try {
    const json = { ...body }
    const { innerWidth: width, innerHeight: height } = window
    if (
      [width, height].every(
        value => Number.isInteger(value) && value >= 320 && value <= 8192
      )
    ) {
      json.viewport = { width, height }
    }
    const bundle = bundleId()
    if (bundle === null) {
      delete json.bundle
    } else {
      json.bundle = bundle
    }
    const answer = await api
      .post('display-heartbeat', { json })
      .json<HeartbeatAnswer>()
    return answer && typeof answer.lastSeenAt === 'string' ? answer : null
  } catch {
    return null
  }
}
